import { resolveTheme } from '@shared/themes';
import { Injectable, inject, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { Observable, Subject } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConfirmDialogComponent, IConfirmDialog } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { map, take } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { IStatus } from 'app/models/licenses/status';
import { ILicenseChangeDialog, InHomeCareChangeDialogComponent } from 'app/components/in-home-care/in-home-care-change/in-home-care-change-dialog.component';
import { AuditLogComponent } from 'app/components/reusable/audit-log/audit-log.component';
import { PromptDialogComponent } from '../components/prompt-dialog/prompt-dialog.component';

@Injectable({ providedIn: 'root' })
export class SharedService {
  public eventEmitter = new EventEmitter<any>();
  public mobile: boolean = false;
  public tablet: boolean = false;
  public development: boolean = environment.build === 'Development';
  private api: string = environment.privateApi;
  private changeTheme: Subject<string> = new Subject<string>();
  public get darkMode(): boolean { return resolveTheme(this.theme).mode === 'dark'; }
  public theme: string = 'light';
  private promptSubject = new Subject<string | null>();

  /**
   * Color Codes
   */
  public purple: string = '#9355dc';
  public blue: string = '#628ef4';
  public gold: string = '#ffbe2e';
  public green: string = '#70e17b';
  public pink: string = '#e9695f';
  public orange: string = '#EC891D';
  public red: string = '#fc4132';
  public loading: boolean = false;
  public today: Date = new Date(Date.now());

  /**
   * License Type Names
   */
  public readonly hemoTechLicenseName = 'Hemo Tech';

  /**
   * Entity Types
   */
  public inHomeCareLicense: string = 'InHomeCareLicense';
  public inHomeCareChange: string = 'InHomeCareChange';
  public hemoTechLicense: string = 'HemoTechLicense';
  public hemoTechChange: string = 'HemoTechChange';
  public hospitalLicense: string = 'HospitalLicense';
  public onCampusBuilding: string = 'OnCampusBuilding';
  public satelliteLocation: string = 'SatelliteLocation';
  public entityOwner: string = 'EntityOwner';
  public branchOperation: string = 'BranchOperation';
  public complaint: string = 'Complaint';
  public administratorApplication: string = 'IHCAdministratorApplication';
  public professionalExperience: string = 'IHCProfessionalExperience';
  public managementExperience: string = 'IHCManagementExperience';
  public classification: string = 'Classification';
  public nurse: string = 'Nurse';
  public parent: string = 'Parent';
  public address: string = 'Address';
  public reference: string = 'Reference';
  public entityTypeLicense: string = 'LicenseMaster';

  /**
   * Readable Entity Types
   */
  public readableEntityTypes: { [key: string]: string } = {
    'InHomeCareLicense': 'In Home Care License',
    'HemoTechLicense': 'HemoTech License',
    'HemoTechChange': 'HemoTech Change',
    'InHomeCareChange': 'In Home Care Change',
    'EntityOwner': 'Entity Owner',
    'BranchOperation': 'Branch Operation',
    'IHCAdministratorApplication': 'Administrator Application',
    'IHCProfessionalExperience': 'Professional Experience',
    'IHCManagementExperience': 'Management Experience',
    'Classification': 'Classification',
    'Nurse': 'Nurse',
    'Parent': 'Parent',
    'Address': 'Physical Address'
  };

  /**
  * Classification Types
  */
  public limited: string = 'Limited';
  public basic: string = 'Basic';
  public intermediate: string = 'Intermediate';
  public comprehensive: string = 'Comprehensive';

  /**
   * Change Types
   */
  public add: string = 'Add';
  public change: string = 'Change';
  public delete: string = 'Delete';

  /*
   * Reference Document Types
   */
  public emptyBackgroundCheck: string = 'Empty Background Check Request';
  public fingerprints: string = 'Fingerprints';
  public policyAndProcedure: string = 'Policy and Procedure Memo';

  /*
   * Statuses
   */
  public started: string = "Started";
  public submitted: string = "Submitted";
  public submittedForApproval: string = 'Submitted for Approval';
  public resubmitted: string = "Resubmitted";
  public awaitingPayment: string = "Approved, Pending Payment and Clearance";
  public approved: string = "Approved";
  public renewal: string = "Renewal";
  public incomplete: string = "Incomplete";
  public insufficient: string = "Insufficient";
  public underReview: string = "Under Review";
  public denied: string = "Denied";
  public revoked: string = "Revoked";
  public expired: string = "Expired";
  public withdrawn: string = "Withdrawn";
  public surrendered: string = "Surrendered";
  public closed: string = 'Closed';

  public statusList: IStatus[] = [];

  public statuses: string[] = [
    this.submitted, this.resubmitted, this.submittedForApproval, this.awaitingPayment, this.renewal,
    this.approved, this.incomplete, this.insufficient, this.underReview, this.started, this.denied, this.revoked,
    this.expired, this.withdrawn, this.surrendered, this.closed
  ];

  stateList: string[] = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL",
    "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT",
    "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
    "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "American Samoa",
    "Commonwealth of the Northern Mariana Islands", "Guam", "Puerto Rico", "US Virgin Islands",
    "Federated States of Micronesia", "Republic of the Marshall Islands", "Republic of Palau"
  ];

  oregonCounties: string[] = [
    "Baker", "Benton", "Clackamas", "Clatsop", "Columbia", "Coos", "Crook",
    "Curry", "Deschutes", "Douglas", "Gilliam", "Grant", "Harney", "Hood River",
    "Jackson", "Jefferson", "Josephine", "Klamath", "Lake", "Lane", "Lincoln",
    "Linn", "Malheur", "Marion", "Morrow", "Multnomah", "Polk", "Sherman",
    "Tillamook", "Umatilla", "Union", "Wallowa", "Wasco", "Washington", "Wheeler",
    "Yamhill"
  ];

  washingtonCounties: string[] = [
    "Adams", "Asotin", "Benton", "Chelan", "Clallam", "Clark", "Columbia",
    "Cowlitz", "Douglas", "Ferry", "Franklin", "Garfield", "Grant", "Grays Harbor",
    "Island", "Jefferson", "King", "Kitsap", "Kittitas", "Klickitat", "Lewis",
    "Lincoln", "Mason", "Okanogan", "Pacific", "Pend Oreille", "Pierce", "San Juan",
    "Skagit", "Skamania", "Snohomish", "Spokane", "Stevens", "Thurston", "Wahkiakum",
    "Walla Walla", "Whatcom", "Whitman", "Yakima"
  ];

  idahoCounties: string[] = [
    "Ada", "Adams", "Bannock", "Bear Lake", "Benewah", "Bingham", "Blaine",
    "Boise", "Bonner", "Bonneville", "Boundary", "Butte", "Camas", "Canyon",
    "Caribou", "Cassia", "Clark", "Clearwater", "Custer", "Elmore", "Franklin",
    "Fremont", "Gem", "Gooding", "Idaho", "Jefferson", "Jerome", "Kootenai",
    "Latah", "Lemhi", "Lewis", "Lincoln", "Madison", "Minidoka", "Nez Perce",
    "Oneida", "Owyhee", "Payette", "Power", "Shoshone", "Teton", "Twin Falls",
    "Valley", "Washington"
  ];

  californiaCounties: string[] = [
    "Alameda", "Alpine", "Amador", "Butte", "Calaveras", "Colusa", "Contra Costa",
    "Del Norte", "El Dorado", "Fresno", "Glenn", "Humboldt", "Imperial", "Inyo",
    "Kern", "Kings", "Lake", "Lassen", "Los Angeles", "Madera", "Marin", "Mariposa",
    "Mendocino", "Merced", "Modoc", "Mono", "Monterey", "Napa", "Nevada", "Orange",
    "Placer", "Plumas", "Riverside", "Sacramento", "San Benito", "San Bernardino",
    "San Diego", "San Francisco", "San Joaquin", "San Luis Obispo", "San Mateo",
    "Santa Barbara", "Santa Clara", "Santa Cruz", "Shasta", "Sierra", "Siskiyou",
    "Solano", "Sonoma", "Stanislaus", "Sutter", "Tehama", "Trinity", "Tulare",
    "Tuolumne", "Ventura", "Yolo", "Yuba"
  ];

  countiesByState: { [key: string]: string[] } = {
    "OR": this.oregonCounties,
    "WA": this.washingtonCounties,
    "ID": this.idahoCounties,
    "CA": this.californiaCounties,
    "": []
  };

  public confirmDialogRef!: MatDialogRef<ConfirmDialogComponent>;
  private httpClient = inject(HttpClient);
  public dialog = inject(MatDialog);
  public toastr = inject(ToastrService);

  public openNotice(message: string): void {
    let data: IConfirmDialog = {
      message: message,
      confirmation: false,
      darkMode: this.darkMode
    }
    this.openDialog(data);
  }

  openPrompt(message: string, title: string = 'Input Required', placeholder: string = ''): void {
    const dialogRef = this.dialog.open(PromptDialogComponent, {
      width: '400px',
      data: {
        title: title,
        message: message,
        placeholder: placeholder
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      this.promptSubject.next(result || null);
    });
  }

  promptCompleted(): Observable<string | null> {
    return this.promptSubject.asObservable();
  }

  public openConfirm(message: string): void {
    let data: IConfirmDialog = {
      message: message,
      confirmation: true,
      darkMode: this.darkMode
    }
    this.openDialog(data);
  }

  openDialog(data: IConfirmDialog): void {
    this.confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: data,
      role: 'dialog',
      ariaLabel: "Confirmation Dialog",
      minWidth: '300px',
      maxWidth: !this.mobile ? '700px' : '300px',
      maxHeight: this.mobile ? '600px' : '700px',

    });
  }

  public confirmed(): Observable<any> {
    return this.confirmDialogRef.afterClosed().pipe(take(1), map(response => {
      return response;
    }));
  }

  requestChange(dialogData: ILicenseChangeDialog): void {
    this.dialog.open(InHomeCareChangeDialogComponent, {
      data: dialogData,
      role: 'dialog',
      ariaLabel: "Change Request Dialog",
      minWidth: !this.mobile ? '800px' : '',
      maxWidth: !this.mobile ? '1000px' : '300px',

    });
  }

  openAuditLog(entityKey: number, entityType: string): void {
    this.dialog.open(AuditLogComponent, {
      data: { entityKey: entityKey, entityType: entityType },
      role: 'dialog',
      ariaLabel: "Audit Log Dialog",
      minWidth: '850px',
      maxWidth: '1000px',

    });
  }

  get isMobile(): boolean {
    return this.mobile;
  }

  get counties(): string[] {
    return this.oregonCounties;
  }

  get states(): string[] {
    return this.stateList;
  }

  themeChanged$ = this.changeTheme.asObservable();

  getUserTheme(): void {
    this.httpClient.get<string>(this.api + "UserProfile/GetUserTheme", { responseType: 'text' as 'json' }).subscribe({
      next: response => {
        this.theme = response;
        this.toggleTheme();
      },
      error: e => console.log('error getting user theme', e)
    });
  }

  updateUserTheme(): void {
    this.httpClient.post<any>(this.api + "UserProfile/UpdateUserTheme/" + this.theme, null).subscribe({
      complete: () => { },
      error: e => console.log('error updating user theme', e)
    });
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text);
    this.toastr.success("Copied!");
  }

  toggleTheme() {
    this.theme = resolveTheme(this.theme).id;
    this.changeTheme.next(this.theme);
  }

  consoleLog(error: any): void {
    if (this.development) {
      console.log('error', error);
    }
  }

  getStatusesByLicenseType(licenseTypeId: string): Observable<IStatus[]> {
    return this.httpClient.get<IStatus[]>(this.api + "License/GetLicenseStatuses/" + licenseTypeId).pipe(
      map(response => {
        this.statusList = response;
        return response;
      })
    );
  }

  getStatuses(): void {
    if (this.statusList.length > 0) return;
    this.httpClient.get<IStatus[]>(this.api + "Status/GetStatuses/" + false).subscribe({
      next: response => {
        this.statusList = response;
      },
      error: e => console.log('error getting statuses', e)
    });
  }

  isCancelled(response: any): boolean {
    return response === 'cancel' || response === null || response === '' || response === undefined;
  }

  getDateFromString(date: string): Date {
    const dateComponents = date.split('/');
    const month = parseInt(dateComponents[0], 10);
    const day = parseInt(dateComponents[1], 10);
    const year = parseInt(dateComponents[2], 10);
    return new Date(year, month - 1, day);
  }
}
